import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  LinearProgress,
} from '@mui/material'
import { FormEvent, FormEventHandler, memo, MouseEventHandler, ReactNode, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

export interface FormDialogActionsProps {
  onCancel: MouseEventHandler<HTMLButtonElement>
  children?: ReactNode
  confirmLabel?: string
  confirmColor?: ButtonProps['color']
  disabled?: boolean
  loading?: boolean
  additionalActions?: ReactNode
}

const FormDialogActions = ({
  onCancel,
  confirmLabel,
  confirmColor,
  disabled,
  loading,
  additionalActions,
}: FormDialogActionsProps) => {
  const { t } = useTranslation()
  return (
    <DialogActions>
      {additionalActions}
      <Button disabled={loading} color="inherit" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={disabled} loading={loading} color={confirmColor}>
        {confirmLabel ?? t('common.confirm')}
      </Button>
    </DialogActions>
  )
}

export interface FormDialogProps extends Omit<DialogProps, 'title' | 'component' | 'slotProps' | 'onSubmit'> {
  onConfirm: (event: FormEvent<HTMLFormElement>) => void
  title: ReactNode
  confirmLabel?: string
  confirmColor?: ButtonProps['color']
  disabled?: boolean
  loading?: boolean
  dividers?: boolean
  disableGutterContent?: boolean
  disableContentPaddingTop?: boolean
  additionalActions?: ReactNode
  showLoadingBar?: boolean
}

const FormDialog = ({
  onClose,
  onConfirm,
  title,
  children,
  open,
  confirmLabel,
  confirmColor = 'primary',
  disabled = false,
  loading = false,
  dividers = false,
  disableGutterContent = false,
  disableContentPaddingTop = false,
  showLoadingBar = false,
  additionalActions,
  ...dialogProps
}: FormDialogProps): JSX.Element => {
  const handleClose: DialogProps['onClose'] = (e, reason) => {
    if (!loading && onClose) onClose(e, reason)
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!loading) onConfirm(e)
  }

  return (
    <Dialog
      {...dialogProps}
      onClose={handleClose}
      open={open}
      slotProps={{ paper: { component: 'form', onSubmit: handleSubmit } }}
    >
      <DialogTitle>{title}</DialogTitle>
      {/* Show loading bar with a placeholder to maintain layout height */}
      {showLoadingBar ? loading ? <LinearProgress /> : <span style={{ height: '4px' }} /> : null}
      <DialogContent
        dividers={dividers}
        style={{ padding: disableGutterContent ? 0 : undefined, paddingTop: disableContentPaddingTop ? 0 : undefined }}
      >
        {/* This is to make space for the label of inputs that use negative margins */}
        {/* When dividers are enabled there is already a padding top applied */}
        <Box pt={disableGutterContent || dividers ? 0 : 1}>{children}</Box>
      </DialogContent>
      <FormDialogActions
        onCancel={(e) => handleClose(e, 'escapeKeyDown')}
        confirmLabel={confirmLabel}
        confirmColor={confirmColor}
        disabled={disabled}
        loading={loading}
        additionalActions={additionalActions}
      />
    </Dialog>
  )
}

export default memo(FormDialog)
